import { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { AlertCircle, CheckCircle2, ChevronDown, ChevronUp, Database, Info, Loader2, Sparkles, Wand2 } from "lucide-react";
import { rawFetch } from "@/lib/api/client";
import { PageHeader, ErrorBox, DataTable } from "@/components/common";
import { Button, Tabs } from "@/components/ui-kit";
import { RetrievalProfileBuilder } from "@/components/RetrievalProfileBuilder";
import { RetrievalReadiness, useRetrievalReadiness } from "@/components/RetrievalReadiness";
import { RetrievalConnect, RetrievalEvaluate } from "@/components/RetrievalJourneyPanels";
import { useApp } from "@/lib/store";
import { useWorkspacePermissions } from "@/lib/workspacePermissions";

interface Profile { id: string; name: string; description?: string; modes?: Array<{ key: string }> }
interface Hit { id: string; kind: string; source: string; score: number; doc_id?: string; chunk_id?: string }
interface Citation { id: string; title?: string; definition?: string; uri?: string; kind?: string; source?: string }
interface RouteExplain { strategy: string; modes?: { key: string }[]; reason?: string; signals?: Record<string, unknown>; matched_terms?: string[];
  ranking?: { candidates_adjusted: number; metadata_boosts: number; source_trust_weights: number; recency_half_life_days: number };
  rerank?: { status: string; reason?: string; reranked?: number } }
interface ExpertEvidence { path: string; topic?: string; weight: number }
interface ExpertHit { person_id: string; person_label: string; score: number; evidence: ExpertEvidence[] }

interface DiagnosticsCheck { key: string; label: string; ok: boolean; detail: string; hint: string }
interface DiagnosticsSource { name: string; connector_key: string; status: string; contributes_people: boolean }
interface ExpertsDiagnostics {
  can_run: boolean;
  checks: DiagnosticsCheck[];
  graph: {
    persons: number; topics: number; decisions: number; documents: number; meetings: number;
    edges_expert_in: number; edges_authored: number; edges_attended: number; edges_decided_by: number;
  };
  sources: DiagnosticsSource[];
  method: { algorithm: string; recency_half_life_days: number; weights: Record<string, number>; paths: string[] };
  llm: { provider: string; model: string; redaction_enabled: boolean; posture: string };
}

export function Retrieval() {
  const ws = useApp((s) => s.workspaceId);
  const { canEdit } = useWorkspacePermissions();
  const seededWorkspace = useRef("");
  const qc = useQueryClient();
  const [activeTab, setActiveTab] = useState<"configure" | "test" | "evaluate" | "connect" | "org">("test");
  const [profileId, setProfileId] = useState(() => new URLSearchParams(window.location.search).get("profile") || "");
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState<Hit[] | null>(null);
  const [citations, setCitations] = useState<Citation[]>([]);
  const [explain, setExplain] = useState<RouteExplain | null>(null);
  const readiness = useRetrievalReadiness(profileId);

  const profiles = useQuery<{ results: Profile[] }>({
    queryKey: ["profiles", ws],
    queryFn: () => rawFetch(`/retrieval/profiles/?page_size=100${ws ? `&workspace=${ws}` : ""}`),
  });
  const activeProfile = profiles.data?.results.find((profile) => profile.id === profileId);
  const readyForSelectedMethods = Boolean(readiness.data && activeProfile?.modes?.some((mode) => {
    if (mode.key === "term_definitions") return readiness.data!.glossary.available;
    if (mode.key === "bm25") return readiness.data!.documents.available;
    if (mode.key === "vector") return readiness.data!.vector.available;
    if (mode.key === "graph" || mode.key === "graph.multihop") return readiness.data!.graph.available;
    return false;
  }));
  useEffect(() => {
    if (!ws || !profiles.data) return;
    if (profiles.data.results.length) {
      if (!profileId || !profiles.data.results.some((row) => row.id === profileId)) setProfileId(profiles.data.results[0].id);
      return;
    }
    if (!canEdit || seededWorkspace.current === ws) return;
    seededWorkspace.current = ws;
    rawFetch(`/retrieval/profiles/ensure-default/?workspace=${encodeURIComponent(ws)}`, {
      method: "POST", body: JSON.stringify({ workspace: ws }),
    }).then(() => qc.invalidateQueries({ queryKey: ["profiles", ws] }))
      .catch(() => { seededWorkspace.current = ""; });
  }, [ws, canEdit, profiles.data, profileId, qc]);

  const search = useMutation({
    mutationFn: (question: string) => rawFetch<{ hits: Hit[]; citations: Citation[]; explain?: RouteExplain }>("/retrieval/search/", {
      method: "POST",
      body: JSON.stringify({ profile_id: profileId, query: question, top_k: 10 }),
    }),
    onSuccess: (data) => {
      setHits(data.hits ?? []);
      setCitations(data.citations ?? []);
      // Phase 13.6 / G16 — the flat search response now carries the router
      // `explain` trace directly, so we no longer need a second round-trip
      // to the profile `query` action just to render the "why this strategy".
      setExplain(data.explain?.strategy ? data.explain : null);
    },
  });

  function runSearch(question = query) {
    setQuery(question);
    setExplain(null);
    search.mutate(question);
  }


  return (
    <>
      <PageHeader title="Retrieval" subtitle="Choose what to search, ask a question and see where each result came from."
        help={
          <>
            <p>A profile chooses approved terms and permitted documents for your workspace. Every document result respects the reader’s access.</p>
          </>
        }
        learnMore={{ to: "/howto", hash: "retrieval", label: "How retrieval works" }} />
      <div className="card mb-4 flex flex-wrap items-center gap-3">
        <label className="text-sm font-medium">Profile
          <select className="input ml-2 min-w-52" value={profileId} onChange={(event) => { setProfileId(event.target.value); setHits(null); setExplain(null); }}>
            <option value="">Choose a profile</option>
            {profiles.data?.results?.map((profile) => <option key={profile.id} value={profile.id}>{profile.name}</option>)}
          </select></label>
        <span className={readyForSelectedMethods ? "pill-good" : "pill-warn"}>
          {!profileId ? "Choose a profile" : readiness.isLoading ? "Checking content" : readyForSelectedMethods ? "Ready to test" : "Selected methods need content"}
        </span>
      </div>
      <div className="mb-4"><Tabs value={activeTab} onChange={setActiveTab} label="Retrieval views" items={[
        { value: "configure", label: "Configure" }, { value: "test", label: "Test" },
        { value: "evaluate", label: "Evaluate" }, { value: "connect", label: "Connect" },
        { value: "org", label: "Org memory" },
      ]} /></div>
      {activeTab === "test" && <section role="tabpanel" aria-label="Test retrieval">
      <RetrievalReadiness profileId={profileId} onSample={runSearch} />
      <div className="card mb-4">
        <label className="mb-2 block text-sm font-medium" htmlFor="retrieval-question">Ask a question</label>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-[1fr_auto]">
          <input id="retrieval-question" className="input" placeholder="Ask about your terms or documents"
                 value={query} onChange={(e) => setQuery(e.target.value)} />
          <Button variant="primary" disabled={!profileId || !query || search.isPending}
                  onClick={() => runSearch()}>
            Search
          </Button>
        </div>
      </div>
      <div className="grid gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(18rem,1fr)]">
      <div>
      {search.isError && <ErrorBox error={search.error} />}
      {hits && (hits.length ? <div className="card mb-4 space-y-2" aria-label="Search results">
        <h2 className="text-sm font-semibold">{hits.length} result{hits.length === 1 ? "" : "s"}</h2>
        {hits.map((hit, index) => { const citation = citations[index]; return <article key={hit.id} className="border-t border-border pt-2 text-sm">
          <strong>{citation?.title || (hit.kind === "term_definition" ? "Approved term" : "Indexed passage")}</strong>
          {citation?.definition && <p className="mt-1 text-muted">{citation.definition}</p>}
          <p className="mt-1 text-xs text-muted">{hit.kind === "term_definition" ? "Approved glossary" : "Permitted document"} · Citation {index + 1}</p>
        </article>; })}
      </div> : <div className="card mb-4" role="status"><h2 className="text-sm font-semibold">No results yet</h2>
        <p className="mt-1 text-sm text-muted">{readiness.data?.glossary.available
          ? `The pinned glossary has ${readiness.data.glossary.terms} approved terms, but none matched this question.`
          : "This profile has no approved glossary terms."} {readiness.data?.documents.chunks
          ? "Try different wording or check your source and metadata filters."
          : "No permitted documents are indexed for this profile."}</p>
        <Link to={readiness.data?.documents.chunks ? "/ontology/term-sets" : "/catalog"} className="mt-2 inline-block text-sm text-accent underline">
          {readiness.data?.documents.chunks ? "Review term sets" : "Add searchable content"}</Link>
      </div>)}
      </div>
      <aside className="xl:sticky xl:top-4 xl:self-start" aria-label="Why these results">
      {explain ? (
        <div className="card mb-4 border-accent/30 bg-accent/5">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Wand2 size={15} className="text-accent" />
            Mode: <span className="text-accent">{explain.strategy === "fixed" ? "Fixed" : explain.strategy === "glossary" ? "Glossary lookup" : explain.strategy === "relationships" ? "Relationships" : "Documents"}</span>
            {(explain.modes?.length ?? 0) > 0 && (
              <span className="text-xs text-muted">
                Searched: {explain.modes!.map((m) => ({ term_definitions: "approved term definitions", bm25: "document wording", vector: "document meaning", graph: "relationships", "graph.multihop": "relationships" })[m.key as "term_definitions" | "bm25" | "vector" | "graph" | "graph.multihop"] || "available content").join(" + ")}
              </span>
            )}
          </div>
          {explain.reason && <p className="mt-1 text-xs text-muted">{explain.reason}</p>}
          {explain.ranking && <p className="mt-1 text-xs text-muted">Your metadata, source trust and recency choices adjusted {explain.ranking.candidates_adjusted} results.</p>}
          {explain.rerank && <p className="mt-1 text-xs text-muted">Workspace AI ranking: {explain.rerank.status === "applied" ? "applied" : "not used; the original order was kept"}.</p>}
          {!!explain.matched_terms?.length && <div className="mt-2 flex flex-wrap gap-1" aria-label="Matched ontology terms">
            {explain.matched_terms.map((term) => <span key={term} className="pill text-[10px]">{term}</span>)}
          </div>}
        </div>
      ) : <div className="card mb-4 text-sm text-muted">Run a question to see why this profile chose its search methods and which terms matched.</div>}
      </aside></div>
      </section>}
      {activeTab === "configure" && <section role="tabpanel" aria-label="Configure retrieval"><RetrievalProfileBuilder workspace={ws} selectedProfileId={profileId} onSelect={setProfileId} /></section>}
      {activeTab === "evaluate" && <section role="tabpanel" aria-label="Evaluate retrieval"><RetrievalEvaluate profileId={profileId} workspace={ws} /></section>}
      {activeTab === "connect" && <section role="tabpanel" aria-label="Connect retrieval"><RetrievalConnect profileId={profileId} workspace={ws} /></section>}
      {activeTab === "org" && <section role="tabpanel" aria-label="Org memory"><ExpertsPanel /></section>}
    </>
  );
}


function ExpertsPanel() {
  const ws = useApp((s) => s.workspaceId);
  const qc = useQueryClient();
  const [topic, setTopic] = useState("");
  const [submitted, setSubmitted] = useState("");
  const [methodOpen, setMethodOpen] = useState(false);
  const [installNotice, setInstallNotice] = useState<string | null>(null);
  const [orgMemoryNotice, setOrgMemoryNotice] = useState<string | null>(null);

  const diag = useQuery<ExpertsDiagnostics>({
    queryKey: ["experts-diagnostics", ws],
    queryFn: () => rawFetch(`/retrieval/experts/diagnostics/?workspace=${ws}`),
    enabled: Boolean(ws),
  });

  const experts = useQuery<{ topic: string; hits: ExpertHit[] }>({
    queryKey: ["experts", ws, submitted],
    queryFn: () =>
      rawFetch(
        `/retrieval/experts/?topic=${encodeURIComponent(submitted)}&limit=10${ws ? `&workspace=${ws}` : ""}`,
      ),
    enabled: Boolean(submitted && ws),
  });

  const installDecisionIntent = useMutation({
    mutationFn: () => rawFetch<{
      ok: boolean;
      modified: Array<{ id: string; name: string; step_id: string }>;
      unchanged: Array<{ id: string; name: string }>;
    }>("/pipelines/install-decision-intent/", {
      method: "POST",
      body: JSON.stringify({ workspace: ws }),
    }),
    onSuccess: (resp) => {
      const m = resp.modified.length;
      const u = resp.unchanged.length;
      setInstallNotice(
        m > 0
          ? `Added Decision-Intent to ${m} pipeline${m === 1 ? "" : "s"}` +
            (u > 0 ? ` (${u} already had it).` : ".")
          : u > 0
            ? "Decision-Intent already present on every pipeline."
            : "No pipelines updated.",
      );
      qc.invalidateQueries({ queryKey: ["experts-diagnostics", ws] });
    },
  });

  const installOrgMemory = useMutation({
    mutationFn: () => rawFetch<{
      installed: boolean;
      action: "reused" | "published_existing_draft" | "seeded_and_published";
      version_id: string;
      semver: string;
      status: string;
      entities_created: number;
      relations_created: number;
      competency_questions_created: number;
    }>("/ontology/install-org-memory/", {
      method: "POST",
      body: JSON.stringify({ workspace: ws }),
    }),
    onSuccess: (resp) => {
      const verb =
        resp.action === "reused" ? "already published" :
        resp.action === "published_existing_draft" ? "published existing draft" :
        "seeded & published";
      setOrgMemoryNotice(
        `Org-Memory ${verb} (v${resp.semver})` +
        (resp.entities_created
          ? ` — ${resp.entities_created} entities, ${resp.relations_created} relations.`
          : "."),
      );
      qc.invalidateQueries({ queryKey: ["experts-diagnostics", ws] });
      qc.invalidateQueries({ queryKey: ["table", "/ontology/versions/"] });
    },
  });

  // Map a check key to its actionable CTA.
  type Cta =
    | { mode: "link"; to: string; label: string }
    | { mode: "install_decision_intent"; label: string }
    | { mode: "install_org_memory"; label: string };
  const ctaFor = (key: string): Cta | null => {
    switch (key) {
      case "ontology":         return { mode: "install_org_memory",       label: "Install Org-Memory (one click)" };
      case "people_sources":   return { mode: "link", to: "/catalog",     label: "Connect a source" };
      case "decision_intent":  return { mode: "install_decision_intent",  label: "Add Decision-Intent to pipelines" };
      case "expertise_signal": return { mode: "link", to: "/catalog",     label: "Ingest documents or meetings" };
      case "llm":              return { mode: "link", to: "/settings",    label: "Configure LLM provider" };
      default: return null;
    }
  };

  const ready = diag.data?.can_run ?? false;
  const blockedReasons = diag.data?.checks.filter((c) => !c.ok && c.key !== "llm") ?? [];

  return (
    <section className="mt-8">
      <div className="mb-3 flex items-center gap-2">
        <Sparkles size={16} className="text-accent" />
        <h2 className="text-base font-semibold">Find expert on a topic</h2>
        <span className="rounded-full border border-border px-2 py-0.5 text-[10px] uppercase tracking-wide text-muted">
          Org-Memory
        </span>
      </div>
      <p className="mb-4 max-w-3xl text-xs text-muted">
        Ranks <code>Person</code> nodes in your tenant graph by topical evidence
        — direct expertise, authored documents, meeting attendance, and
        decision ownership — with recency decay. Every score is traceable
        to graph edges; nothing is hallucinated.
      </p>

      {/* STEP 1 — readiness */}
      <div className="card mb-4">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div className="text-xs font-medium uppercase tracking-wide text-muted">
            Step 1 · Readiness
          </div>
          {diag.isLoading && <Loader2 size={14} className="animate-spin text-muted" />}
          {diag.data && (
            <span className={`rounded px-2 py-0.5 text-[11px] font-medium ${
              ready ? "border border-good/30 bg-good/10 text-good" : "border border-warn/30 bg-warn/10 text-warn"
            }`}>
              {ready ? "Ready to run" : `${blockedReasons.length} prerequisite${blockedReasons.length === 1 ? "" : "s"} pending`}
            </span>
          )}
        </div>
        {diag.isError && <ErrorBox error={diag.error} />}
        {diag.data && (
          <ul className="grid gap-2 md:grid-cols-2">
            {diag.data.checks.map((c) => {
              const cta = !c.ok ? ctaFor(c.key) : null;
              return (
                <li
                  key={c.key}
                  className={`flex items-start gap-2 border p-2.5 text-xs ${
                    c.ok ? "border-good/20 bg-good/5" : c.key === "llm" ? "border-border bg-panel" : "border-warn/30 bg-warn/5"
                  }`}
                >
                  {c.ok ? (
                    <CheckCircle2 size={14} className="mt-0.5 shrink-0 text-good" />
                  ) : c.key === "llm" ? (
                    <Info size={14} className="mt-0.5 shrink-0 text-muted" />
                  ) : (
                    <AlertCircle size={14} className="mt-0.5 shrink-0 text-warn" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-foreground">{c.label}</div>
                    <div className="mt-0.5 text-muted">{c.detail}</div>
                    {!c.ok && (
                      <div className="mt-1 text-[11px] text-muted/80">{c.hint}</div>
                    )}
                    {cta && cta.mode === "link" && (
                      <Link
                        to={cta.to}
                        className="mt-1.5 inline-block rounded-md border border-border bg-background px-2 py-0.5 text-[11px] text-accent hover:bg-accent/10"
                      >
                        {cta.label} →
                      </Link>
                    )}
                    {cta && cta.mode === "install_decision_intent" && (
                      <div className="mt-1.5 flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 rounded-md border border-accent/40 bg-accent/10 px-2 py-0.5 text-[11px] text-accent hover:bg-accent/20 disabled:opacity-50"
                          onClick={() => installDecisionIntent.mutate()}
                          disabled={installDecisionIntent.isPending || !ws}
                          title="Splice enrich.decision_intent into every enabled pipeline"
                        >
                          <Wand2 size={11} />
                          {installDecisionIntent.isPending ? "Installing…" : cta.label}
                        </button>
                        <Link
                          to="/pipelines"
                          className="text-[11px] text-muted underline hover:text-accent"
                        >
                          edit DAG manually →
                        </Link>
                      </div>
                    )}
                    {installDecisionIntent.isError && c.key === "decision_intent" && (
                      <div className="mt-1 text-[11px] text-bad">
                        {String(installDecisionIntent.error instanceof Error
                          ? installDecisionIntent.error.message
                          : installDecisionIntent.error)}
                      </div>
                    )}
                    {installNotice && c.key === "decision_intent" && (
                      <div className="mt-1 text-[11px] text-good">
                        {installNotice}
                      </div>
                    )}
                    {cta && cta.mode === "install_org_memory" && (
                      <div className="mt-1.5 flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 rounded-md border border-accent/40 bg-accent/10 px-2 py-0.5 text-[11px] text-accent hover:bg-accent/20 disabled:opacity-50"
                          onClick={() => installOrgMemory.mutate()}
                          disabled={installOrgMemory.isPending || !ws}
                          title="Seed the Org-Memory upper ontology and publish it as the active schema"
                        >
                          <Wand2 size={11} />
                          {installOrgMemory.isPending ? "Installing…" : cta.label}
                        </button>
                        <Link
                          to="/ontology"
                          className="text-[11px] text-muted underline hover:text-accent"
                        >
                          inspect first →
                        </Link>
                      </div>
                    )}
                    {installOrgMemory.isError && c.key === "ontology" && (
                      <div className="mt-1 text-[11px] text-bad">
                        {String(installOrgMemory.error instanceof Error
                          ? installOrgMemory.error.message
                          : installOrgMemory.error)}
                      </div>
                    )}
                    {orgMemoryNotice && c.key === "ontology" && (
                      <div className="mt-1 text-[11px] text-good">
                        {orgMemoryNotice}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* STEP 2 — sources connected */}
      {diag.data && diag.data.sources.length > 0 && (
        <div className="card mb-4">
          <div className="mb-2 text-xs font-medium uppercase tracking-wide text-muted">
            Step 2 · Sources feeding people & topics
          </div>
          <div className="flex flex-wrap gap-1.5">
            {diag.data.sources.map((s) => (
              <span
                key={s.name}
                className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] ${
                  s.contributes_people
                    ? "border-accent/40 bg-accent/10 text-accent"
                    : "border-border text-muted"
                }`}
                title={`connector: ${s.connector_key} · status: ${s.status}`}
              >
                <Database size={11} />
                {s.name}
                {!s.contributes_people && (
                  <span className="text-[10px] opacity-60">(no people)</span>
                )}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* STEP 3 — query */}
      <div className="card mb-3">
        <div className="mb-2 text-xs font-medium uppercase tracking-wide text-muted">
          Step 3 · Ask
        </div>
        <form
          className="grid grid-cols-1 gap-2 md:grid-cols-[1fr_auto]"
          onSubmit={(e) => {
            e.preventDefault();
            setSubmitted(topic.trim());
          }}
        >
          <input
            className="input"
            placeholder="e.g. postgres migration, MiFID II, ACL hashing…"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            list="experts-topic-suggestions"
          />
          <datalist id="experts-topic-suggestions">
            {(["postgres migration", "ACL hashing", "ontology versioning", "MiFID II", "claims triage", "incident review"]).map((t) => (
              <option key={t} value={t} />
            ))}
          </datalist>
          <button
            className="btn-primary"
            type="submit"
            disabled={!topic.trim() || !ws || (diag.data && !ready)}
            title={!ready && diag.data ? "Resolve prerequisites in Step 1 first" : undefined}
          >
            Find experts
          </button>
        </form>
        {!ready && diag.data && (
          <div className="mt-2 text-[11px] text-amber-400/80">
            <Info size={11} className="mr-1 inline" />
            Search is disabled until the load-bearing prerequisites in Step 1 pass.
          </div>
        )}
      </div>

      {/* Method disclosure — collapsible, no decoration */}
      {diag.data && (
        <div className="mb-4 rounded-md border border-border bg-panel/40">
          <button
            type="button"
            onClick={() => setMethodOpen((v) => !v)}
            className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-xs"
          >
            <span className="flex items-center gap-2">
              <Info size={13} className="text-muted" />
              <span className="font-medium">How ranking works</span>
              <span className="text-muted">·</span>
              <span className="text-muted">{diag.data.method.algorithm}</span>
            </span>
            {methodOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {methodOpen && (
            <div className="border-t border-border px-3 py-3 text-xs">
              <div className="mb-2 text-muted">
                Recency half-life: <strong className="text-foreground">{diag.data.method.recency_half_life_days} days</strong>
                {" · "}LLM: <strong className="text-foreground">{diag.data.llm.provider}/{diag.data.llm.model}</strong>
                {" · "}Posture: <strong className="text-foreground">{diag.data.llm.posture}</strong>
                {diag.data.llm.redaction_enabled && <span> · PII redaction on</span>}
              </div>
              <div className="mb-1 font-medium">Weighted paths</div>
              <ul className="mb-2 space-y-0.5 text-muted">
                {diag.data.method.paths.map((p) => (
                  <li key={p}><code className="font-mono">{p}</code></li>
                ))}
              </ul>
              <div className="mb-1 font-medium">Edge weights</div>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(diag.data.method.weights).map(([k, v]) => (
                  <span key={k} className="rounded border border-border px-1.5 py-0.5 font-mono text-[10px] text-muted">
                    {k} ×{v}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {experts.isError && <ErrorBox error={experts.error} />}
      {experts.data && (
        <DataTable<ExpertHit & { id?: string }>
          rows={experts.data.hits.map((h) => ({ ...h, id: h.person_id }))}
          cols={[
            { header: "Score", width: "80px", cell: (h) => h.score.toFixed(3) },
            { header: "Person", cell: (h) => <strong>{h.person_label}</strong> },
            {
              header: "Evidence",
              cell: (h) => (
                <ul className="space-y-0.5">
                  {h.evidence.slice(0, 4).map((e: ExpertEvidence, i: number) => (
                    <li key={i} className="text-xs text-muted">
                      <code className="font-mono">{e.path}</code>{" "}
                      <span className="opacity-70">×{e.weight.toFixed(2)}</span>
                    </li>
                  ))}
                  {h.evidence.length > 4 && (
                    <li className="text-xs opacity-60">+{h.evidence.length - 4} more…</li>
                  )}
                </ul>
              ),
            },
          ]}
          empty={
            submitted
              ? `No expertise signal found for "${submitted}". Ingest some Decisions, ADRs, or meeting notes that mention this topic.`
              : "Enter a topic to discover the people most associated with it."
          }
        />
      )}
    </section>
  );
}
